import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import {
  DialogShellComponent,
  FilledButtonComponent,
  OutlinedButtonComponent,
  SelectFieldComponent,
} from '@custom-components';
import { InstallationGroupWithAccess } from '@core/models';
import { InstallationGroupService } from '@core/services';

export interface AddInstallationGroupDialogData {
  /** Partner that owns the partnership; only its installation groups are valid attachment targets. */
  ownerPartnerOid: string;
  /** OIDs of installation groups already linked to the partnership; excluded from the picker. */
  existingGroupOids: string[];
}

@Component({
  selector: 'xemex-add-installation-group-dialog',
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatSelectModule,
    MatIconModule,
    TranslateModule,
    DialogShellComponent,
    FilledButtonComponent,
    OutlinedButtonComponent,
    SelectFieldComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './add-installation-group-dialog.component.html',
})
export class AddInstallationGroupDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<AddInstallationGroupDialogComponent>);
  private readonly installationGroupService = inject(InstallationGroupService);
  readonly data = inject<AddInstallationGroupDialogData>(MAT_DIALOG_DATA);

  readonly groupControl = new FormControl<string | null>(null, Validators.required);
  /** Groups owned by the partnership owner that aren't already linked to the partnership. */
  readonly groups = signal<InstallationGroupWithAccess[]>([]);
  readonly isLoading = signal(true);

  constructor() {
    this.installationGroupService
      .getInstallationGroups({ partnerOid: this.data.ownerPartnerOid, limit: 100, offset: 0 })
      .pipe(takeUntilDestroyed())
      .subscribe({
        next: res => {
          const existing = new Set(this.data.existingGroupOids);
          this.groups.set(
            res.installationGroups.filter(g => !existing.has(g.installationGroupOid)),
          );
          this.isLoading.set(false);
        },
        error: () => {
          this.groups.set([]);
          this.isLoading.set(false);
        },
      });
  }

  onAdd(): void {
    if (!this.groupControl.value) {
      this.groupControl.markAsTouched();
      return;
    }
    this.dialogRef.close(this.groupControl.value);
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
